import React from 'react';
import { Badge, Table, Card, CardBody } from 'reactstrap';

interface PayrollSite {
  siteId: string;
  siteName: string;
  projectName: string;
  daysWorked: number;
  totalWageMultiplier: number;
}

interface DailyBreakdownItem {
  date: string;
  siteId: string;
  siteName: string;
  dailyWage: number;
  overtimeHours: number | null;
}

interface LeaveRecord {
  date: string;
  type: string;
}

interface PayrollPersonnel {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  employmentType: 'DAILY' | 'MONTHLY';
  baseDailyWage: number | null;
  monthlySalary: number | null;
  daysWorked: number;
  totalWageMultiplier: number;
  totalOvertimeHours: number;
  overtimePayment: number;
  totalPayment: number;
  leaveDays: number;
  leaves: LeaveRecord[];
  sites: PayrollSite[];
  dailyBreakdown: DailyBreakdownItem[];
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(amount);

const wageLabel = (value: number) => {
  if (value === 1) return 'Tam';
  if (value === 0.5) return 'Yarım';
  return value.toString();
};

const leaveTypeLabels: Record<string, string> = {
  IZIN: 'İzin',
  RAPOR: 'Rapor',
  DEVAMSIZLIK: 'Devamsızlık',
};

const leaveTypeColors: Record<string, string> = {
  IZIN: 'warning',
  RAPOR: 'info',
  DEVAMSIZLIK: 'danger',
};

interface Props {
  personnel: PayrollPersonnel;
}

const PayrollDetailModal: React.FC<Props> = ({ personnel: p }) => {
  return (
    <div>
      {/* Personnel Info */}
      <Card className="mb-3">
        <CardBody className="p-3">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h6 className="mb-1">{p.firstName} {p.lastName}</h6>
              {p.title && <span className="text-muted fs-12">{p.title}</span>}
            </div>
            <Badge color={p.employmentType === 'MONTHLY' ? 'warning' : 'info'}>
              {p.employmentType === 'MONTHLY' ? 'Aylıkçı' : 'Yevmiyeci'}
            </Badge>
          </div>

          <div className="row g-2 mt-2">
            {p.employmentType === 'DAILY' && p.baseDailyWage !== null && (
              <div className="col-6">
                <div className="bg-light rounded p-2 text-center">
                  <p className="text-muted mb-0 fs-12">Günlük Yevmiye</p>
                  <strong>{formatCurrency(p.baseDailyWage)}</strong>
                </div>
              </div>
            )}
            {p.employmentType === 'MONTHLY' && p.monthlySalary !== null && (
              <div className="col-6">
                <div className="bg-light rounded p-2 text-center">
                  <p className="text-muted mb-0 fs-12">Aylık Maaş</p>
                  <strong>{formatCurrency(p.monthlySalary)}</strong>
                </div>
              </div>
            )}
            <div className="col-6">
              <div className="bg-light rounded p-2 text-center">
                <p className="text-muted mb-0 fs-12">Çalışılan Gün</p>
                <strong>{p.daysWorked}</strong>
              </div>
            </div>
            {p.leaveDays > 0 && (
              <div className="col-6">
                <div className="bg-danger-subtle rounded p-2 text-center">
                  <p className="text-muted mb-0 fs-12">İzin/Rapor</p>
                  <strong className="text-danger">{p.leaveDays} gün</strong>
                </div>
              </div>
            )}
            {p.employmentType === 'DAILY' && (
              <div className="col-6">
                <div className="bg-light rounded p-2 text-center">
                  <p className="text-muted mb-0 fs-12">Toplam Çarpan</p>
                  <strong>{p.totalWageMultiplier}</strong>
                </div>
              </div>
            )}
            {p.totalOvertimeHours > 0 && (
              <div className="col-6">
                <div className="bg-warning-subtle rounded p-2 text-center">
                  <p className="text-muted mb-0 fs-12">Mesai ({p.totalOvertimeHours} sa × 1.5)</p>
                  <strong className="text-warning">{formatCurrency(p.overtimePayment)}</strong>
                </div>
              </div>
            )}
            <div className="col-6">
              <div className="bg-success-subtle rounded p-2 text-center">
                <p className="text-muted mb-0 fs-12">Ödenecek Tutar</p>
                <strong className="text-success">{formatCurrency(p.totalPayment)}</strong>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Leave Records */}
      {p.leaves.length > 0 && (
        <Card className="mb-3">
          <CardBody className="p-3">
            <h6 className="mb-3">
              <i className="ri-calendar-close-line me-1"></i>
              İzin/Rapor Kayıtları
            </h6>
            <div className="table-responsive">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th className="text-center">Tür</th>
                  </tr>
                </thead>
                <tbody>
                  {p.leaves
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map((l, idx) => (
                    <tr key={idx}>
                      <td className="fs-12">
                        {new Date(l.date).toLocaleDateString('tr-TR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                        })}
                      </td>
                      <td className="text-center">
                        <Badge color={leaveTypeColors[l.type] || 'secondary'} className="fs-12">
                          {leaveTypeLabels[l.type] || l.type}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Site Breakdown */}
      {p.sites.length > 0 && (
        <Card className="mb-3">
          <CardBody className="p-3">
            <h6 className="mb-3">
              <i className="ri-building-line me-1"></i>
              Şantiye Bazlı Özet
            </h6>
            <div className="table-responsive">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Proje / Şantiye</th>
                    <th className="text-center">Gün</th>
                    <th className="text-center">Çarpan</th>
                  </tr>
                </thead>
                <tbody>
                  {p.sites.map((s) => (
                    <tr key={s.siteId}>
                      <td className="fs-12">{s.projectName} / {s.siteName}</td>
                      <td className="text-center">{s.daysWorked}</td>
                      <td className="text-center">{s.totalWageMultiplier}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Daily Breakdown */}
      {p.dailyBreakdown.length > 0 && (
        <Card className="mb-0">
          <CardBody className="p-3">
            <h6 className="mb-3">
              <i className="ri-calendar-check-line me-1"></i>
              Günlük Döküm
            </h6>
            <div className="table-responsive">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Şantiye</th>
                    <th className="text-center">Yevmiye</th>
                    <th className="text-center">Mesai</th>
                  </tr>
                </thead>
                <tbody>
                  {p.dailyBreakdown.map((d, idx) => (
                    <tr key={idx}>
                      <td className="fs-12">
                        {new Date(d.date).toLocaleDateString('tr-TR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                        })}
                      </td>
                      <td className="fs-12">{d.siteName}</td>
                      <td className="text-center">
                        <Badge color={d.dailyWage >= 1 ? 'success' : 'warning'} className="fs-12">
                          {wageLabel(d.dailyWage)}
                        </Badge>
                      </td>
                      <td className="text-center">
                        {d.overtimeHours ? (
                          <Badge color="warning" className="fs-12">{d.overtimeHours} sa</Badge>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default PayrollDetailModal;
